/**
 * Phase 3b — Push notification adapter.
 *
 * Wiring is in place; **live delivery is blocked on credentials** (APNs
 * AuthKey + Team ID + Key ID for iOS; FCM service-account JSON for Android).
 * Until those are configured server-side, the existing
 * `notifications.push_to_driver` stub returns
 * `{sent:false, reason:"mobile_push_not_yet_wired"}` and this client-side
 * adapter records the device token via the existing endpoint:
 *
 *   POST /api/driver/push/subscribe
 *   body: { endpoint, keys: {p256dh, auth}, user_agent }
 *
 * For native we store the FCM/APNs token under `keys.token` and prefix the
 * endpoint with `apns:` or `fcm:` so the server can dispatch on the right
 * channel later. No backend change required to start collecting tokens today.
 *
 * Public API:
 *   register({ token, apiBase }) -> Promise<{registered:boolean, reason?:string}>
 *   unregister({ token, apiBase }) -> Promise<{deleted:number}>
 *   onNotification(cb) -> unsubscribeFn   // foreground notification callback
 */
import axios from "axios";
import { isNative, getPlatform } from "./platform";

async function _serverSubscribe(apiBase, token, body) {
  try {
    const res = await axios.post(`${apiBase}/api/driver/push/subscribe`, body, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });
    return res.status >= 200 && res.status < 300;
  } catch {
    return false;
  }
}

async function _serverUnsubscribe(apiBase, token) {
  try {
    const res = await axios.delete(`${apiBase}/api/driver/push/subscribe`, {
      headers: { Authorization: `Bearer ${token}` },
      validateStatus: () => true,
    });
    if (res.status >= 200 && res.status < 300) {
      return res.data?.deleted || 0;
    }
  } catch { /* ignore */ }
  return 0;
}

export async function register({ token, apiBase } = {}) {
  if (!token || !apiBase) return { registered: false, reason: "missing_params" };
  if (!(await isNative())) {
    // Web push uses VAPID — not yet wired. Phase 4 will add the service-worker
    // path. For now we only register native tokens.
    return { registered: false, reason: "web_push_not_yet_implemented" };
  }
  let plugin;
  try {
    const mod = await import("@capacitor/push-notifications");
    plugin = mod?.PushNotifications;
  } catch {
    return { registered: false, reason: "plugin_not_installed" };
  }
  if (!plugin) return { registered: false, reason: "plugin_unavailable" };

  // Request permission
  const perm = await plugin.requestPermissions();
  if (perm.receive !== "granted") {
    return { registered: false, reason: `permission_${perm.receive}` };
  }
  // Register with APNs/FCM
  await plugin.register();

  return new Promise((resolve) => {
    let resolved = false;
    const handler = async (token_event) => {
      if (resolved) return;
      resolved = true;
      const platform = await getPlatform();
      const ok = await _serverSubscribe(apiBase, token, {
        endpoint: `${platform === "ios" ? "apns" : "fcm"}:${token_event.value}`,
        keys: { token: token_event.value },
        user_agent: navigator?.userAgent || `capacitor:${platform}`,
      });
      resolve({ registered: ok, reason: ok ? undefined : "server_save_failed", token: token_event.value });
    };
    plugin.addListener("registration", handler);
    plugin.addListener("registrationError", (err) => {
      if (resolved) return;
      resolved = true;
      resolve({ registered: false, reason: `register_error:${err?.error || "unknown"}` });
    });
    // Safety timeout
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        resolve({ registered: false, reason: "timeout" });
      }
    }, 15000);
  });
}

export async function unregister({ token, apiBase } = {}) {
  if (!token || !apiBase) return { deleted: 0 };
  return { deleted: await _serverUnsubscribe(apiBase, token) };
}

export async function onNotification(cb) {
  if (!(await isNative())) return () => {};
  let plugin;
  try {
    const mod = await import("@capacitor/push-notifications");
    plugin = mod?.PushNotifications;
  } catch {
    return () => {};
  }
  if (!plugin) return () => {};
  const h1 = await plugin.addListener("pushNotificationReceived", cb);
  const h2 = await plugin.addListener("pushNotificationActionPerformed", cb);
  return () => {
    try { h1?.remove?.(); } catch { /* ignore */ }
    try { h2?.remove?.(); } catch { /* ignore */ }
  };
}
